/**
 * GoLivePage — start a broadcast from a phone, in one tap.
 *
 * Route: /admin/churches/:churchId/go-live
 *
 * Deliberately not a dashboard. A title, a box to paste the watch link from
 * whatever platform the church is streaming on, and one button. The link is
 * checked against the same detector the public page uses, so this screen
 * cannot promise a player the page will not produce.
 */
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Radio, Loader2, ArrowLeft, ExternalLink, CheckCircle2,
  AlertTriangle, Square, Clipboard, Camera, Link2, SwitchCamera, Users, Gauge,
} from 'lucide-react';
import { useChurchPublisher } from '@/lib/churchLiveRtc';
import { useLiveTransport, useLiveKitPublisher } from '@/lib/liveKit';
import { CameraTrouble } from '@/components/live/CameraTrouble';
import { PreFlightCheck } from '@/components/live/PreFlightCheck';
import { SimulcastPanel } from '@/components/live/SimulcastPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Named so it is a hook, not a callback: the church screen picks between the
// shared LiveKit publisher and the phase-1 mesh one at mount.
const useChurchLiveKitPublisher = (churchId) =>
  useLiveKitPublisher('church', churchId);

const api = async (path, opts = {}) => {
  // iter 648 — this read `admin_token` first and nothing else the app sets, so
  // every call from this screen went out unauthenticated for an ordinary
  // church admin. The session lives under `eventflow_token`.
  const token = localStorage.getItem('admin_token')
    || localStorage.getItem('eventflow_token') || localStorage.getItem('token');
  const res = await fetch(`${API_URL}${path}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(opts.headers || {}),
    },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.detail || `Request failed (${res.status})`);
  return body;
};

/** What the pasted link will actually do, checked by the backend as you type. */
function LinkVerdict({ url }) {
  const [verdict, setVerdict] = useState(null);
  useEffect(() => {
    const trimmed = (url || '').trim();
    if (!trimmed) { setVerdict(null); return; }
    const timer = setTimeout(() => {
      api('/api/church/stream-url/preview', {
        method: 'POST', body: JSON.stringify({ url: trimmed }),
      }).then(setVerdict).catch(() => setVerdict(null));
    }, 400);
    return () => clearTimeout(timer);
  }, [url]);

  if (!verdict) return null;
  const playable = verdict.kind === 'iframe' || verdict.kind === 'video';
  return (
    <div className={`mt-2 rounded-lg p-3 text-sm flex gap-2 ${
      playable ? 'bg-emerald-50 text-emerald-900' : 'bg-amber-50 text-amber-900'}`}
      data-testid="link-verdict">
      {playable
        ? <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-emerald-600" />
        : <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" />}
      <div>
        <p className="font-medium">
          {playable
            ? `Plays on your page — ${verdict.label || verdict.platform}`
            : 'Will not play inside your page'}
        </p>
        {verdict.note && <p className="mt-0.5 opacity-80">{verdict.note}</p>}
      </div>
    </div>
  );
}


/**
 * Broadcast straight from the phone camera. Three taps: open this screen,
 * type a title, go live.
 *
 * The preview is deliberately shown before going live, so whoever is holding
 * the phone can see the framing without the congregation seeing it first.
 */
function PhoneBroadcast({ churchId, usePublisher, livekit, onLive, onStopped }) {
  const [title, setTitle] = useState('');
  const [cameraOn, setCameraOn] = useState(false);
  const [checking, setChecking] = useState(false);
  const pub = usePublisher(churchId);
  const qc = useQueryClient();

  const start = async () => {
    if (!cameraOn && !(await pub.openCamera())) return;
    // Order matters: the server clears any stale LiveKit room when the
    // broadcast record is created, so connecting first got the publisher
    // kicked out again ("leave request … ROOM_DELETED") a second later.
    // The row going live before the picture arrives is honest — the public
    // page says "starting shortly" until media actually reaches the server.
    try {
      await api(`/api/church/${churchId}/broadcast/start`, {
        method: 'POST', body: JSON.stringify({ title: title.trim() }),
      });
    } catch (e) {
      toast.error(e.message);
      return;
    }
    const ok = await pub.goLive();
    if (!ok) {
      await api(`/api/church/${churchId}/broadcast/stop`, { method: 'POST' })
        .catch(() => { /* the row is ended by the next start anyway */ });
      qc.invalidateQueries({ queryKey: ['live-now', churchId] });
      return;
    }
    toast.success("You're live on the church page");
    onLive?.();
    qc.invalidateQueries({ queryKey: ['live-now', churchId] });
  };

  const stop = async () => {
    pub.stop();
    try {
      await api(`/api/church/${churchId}/broadcast/stop`, { method: 'POST' });
      toast.success('Broadcast ended');
      onStopped?.();
      qc.invalidateQueries({ queryKey: ['live-now', churchId] });
    } catch (e) {
      toast.error(e.message);
    }
  };

  const live = pub.status === 'live';

  return (
    <div className="space-y-4" data-testid="phone-broadcast">
      <div className="relative aspect-[3/4] w-full bg-black rounded-xl overflow-hidden">
        <video ref={pub.attach} autoPlay playsInline muted
               className="w-full h-full object-cover"
               data-testid="camera-preview" />
        {!cameraOn && (
          <button onClick={async () => setCameraOn(await pub.openCamera())}
                  className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-slate-400"
                  data-testid="open-camera-btn">
            <Camera className="h-8 w-8" />
            <span className="text-sm">Tap to turn on the camera</span>
          </button>
        )}
        {live && (
          <>
            <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 rounded-full bg-rose-600 px-2.5 py-1 text-xs font-semibold">
              <span className="h-2 w-2 rounded-full bg-white animate-pulse" />LIVE
            </span>
            <span className="absolute top-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-black/60 px-2.5 py-1 text-xs"
                  data-testid="viewer-count">
              <Users className="h-3 w-3" />{pub.viewers}
            </span>
          </>
        )}
        {cameraOn && (
          <button onClick={() => pub.openCamera(pub.facing === 'user' ? 'environment' : 'user')}
                  className="absolute bottom-3 right-3 rounded-full bg-black/60 p-2.5"
                  data-testid="flip-camera-btn">
            <SwitchCamera className="h-4 w-4" />
          </button>
        )}
      </div>

      <CameraTrouble error={pub.error} steps={pub.errorSteps}
                     onRetry={async () => setCameraOn(await pub.openCamera())} />

      {cameraOn && pub.hasAudio === false && (
        <p className="text-xs text-amber-400/90 text-center" data-testid="no-audio-note">
          The microphone is not available, so this will go out without sound.
        </p>
      )}

      {!live && (
        <div>
          <Label className="text-slate-300">What are you streaming?</Label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)}
                 placeholder="Sunday Morning Service"
                 className="mt-1.5 bg-slate-950 border-slate-700"
                 data-testid="broadcast-title" />
        </div>
      )}

      {live ? (
        <Button variant="destructive" className="w-full h-14 text-base"
                onClick={stop} data-testid="stop-broadcast-btn">
          <Square className="h-5 w-5 mr-2" />Stop the stream
        </Button>
      ) : (
        <>
          <Button variant="outline"
                  className="w-full h-11 border-slate-700 bg-transparent text-slate-200"
                  onClick={() => setChecking(true)} data-testid="preflight-btn">
            <Gauge className="h-4 w-4 mr-2" />Check camera and sound
          </Button>
          <Button className="w-full h-14 text-base bg-rose-600 hover:bg-rose-500"
                  disabled={pub.status === 'starting'}
                  onClick={start} data-testid="start-broadcast-btn">
            {pub.status === 'starting'
              ? <Loader2 className="h-5 w-5 mr-2 animate-spin" />
              : <Radio className="h-5 w-5 mr-2" />}
            Start the stream
          </Button>
        </>
      )}

      <PreFlightCheck open={checking} onOpenChange={setChecking}
                      onPassed={() => pub.openCamera().then(setCameraOn)} />

      {!pub.hasRelay && (
        <p className="text-xs text-amber-400/90 text-center" data-testid="no-relay-warning">
          No relay server is set up, so people on mobile data or office networks
          may not be able to connect.
        </p>
      )}
      <p className="text-xs text-slate-500 text-center" data-testid="transport-note">
        {livekit
          ? 'Relayed by the church streaming server, so the whole congregation can watch.'
          : 'A phone can serve only a handful of viewers at once — it sends the video separately to each one.'}
      </p>
    </div>
  );
}

export default function GoLivePage() {
  const { churchId } = useParams();
  const qc = useQueryClient();
  const transport = useLiveTransport();
  const [title, setTitle] = useState('');
  const [streamUrl, setStreamUrl] = useState('');
  const [mode, setMode] = useState('camera');
  // While this phone is the one broadcasting, the publisher component must
  // stay mounted. The live-now poll turning truthy used to swap the page over
  // to the generic live card, which unmounted the publisher and tore down the
  // camera and socket a second after the broadcast started.
  const [broadcastingHere, setBroadcastingHere] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['live-now', churchId],
    queryFn: () => api(`/api/church/${churchId}/live-now`),
    refetchInterval: 15000,
  });

  const start = useMutation({
    mutationFn: () => api(`/api/church/${churchId}/go-live-now`, {
      method: 'POST',
      body: JSON.stringify({ title: title.trim(), stream_url: streamUrl.trim() }),
    }),
    onSuccess: (r) => {
      toast.success(r.reused_scheduled
        ? "You're live on today's scheduled service"
        : "You're live");
      setTitle(''); setStreamUrl('');
      qc.invalidateQueries({ queryKey: ['live-now', churchId] });
    },
    onError: (e) => toast.error(e.message),
  });

  // Editing the link on a stream that is already on air is a PUT on that
  // stream. It used to call go-live-now, which ends whatever is live and
  // starts something else — so changing the link took the church off air and
  // left a duplicate behind.
  const updateLink = useMutation({
    mutationFn: (id) => api(`/api/church/${churchId}/streams/${id}`, {
      method: 'PUT', body: JSON.stringify({ stream_url: streamUrl.trim() }),
    }),
    onSuccess: () => {
      toast.success('Watch link updated');
      setStreamUrl('');
      qc.invalidateQueries({ queryKey: ['live-now', churchId] });
    },
    onError: (e) => toast.error(e.message),
  });

  const end = useMutation({
    mutationFn: (id) => api(`/api/church/${churchId}/streams/${id}/end`, { method: 'POST' }),
    onSuccess: () => {
      toast.success('Stream ended');
      qc.invalidateQueries({ queryKey: ['live-now', churchId] });
    },
    onError: (e) => toast.error(e.message),
  });

  const paste = async () => {
    try {
      setStreamUrl((await navigator.clipboard.readText()).trim());
    } catch {
      toast.error('Your browser would not share the clipboard — paste it by hand');
    }
  };

  const live = data?.live;
  const publicUrl = data?.public_path ? `${window.location.origin}${data.public_path}` : '';

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 px-4 py-6">
      <div className="max-w-md mx-auto">
        <Link to={`/admin/churches/${churchId}`}
              className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-200 mb-5"
              data-testid="go-live-back">
          <ArrowLeft className="h-3.5 w-3.5" /> {data?.church_name || 'Church'}
        </Link>

        {(isLoading || transport.loading) ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-slate-500" />
          </div>
        ) : (broadcastingHere || (!live && mode === 'camera')) ? (
          <>
            {!broadcastingHere && (
              <>
                <div className="mb-5">
                  <h1 className="text-2xl font-semibold tracking-tight">Go live</h1>
                  <p className="text-sm text-slate-400 mt-1">
                    Broadcast from this phone, or paste a link from a platform you
                    are already streaming on.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-2 mb-4 p-1 bg-slate-900 rounded-lg">
                  <button onClick={() => setMode('camera')}
                          className="flex items-center justify-center gap-1.5 rounded-md py-2 text-sm font-medium bg-slate-700 text-white"
                          data-testid="mode-camera">
                    <Camera className="h-4 w-4" />This phone
                  </button>
                  <button onClick={() => setMode('link')}
                          className="flex items-center justify-center gap-1.5 rounded-md py-2 text-sm font-medium text-slate-400"
                          data-testid="mode-link">
                    <Link2 className="h-4 w-4" />A link
                  </button>
                </div>
              </>
            )}
            <Card className="bg-slate-900 border-slate-800 text-slate-100">
              <CardContent className="p-5">
                <PhoneBroadcast churchId={churchId}
                                key={transport.transport}
                                livekit={transport.livekit}
                                usePublisher={transport.livekit
                                  ? useChurchLiveKitPublisher
                                  : useChurchPublisher}
                                onLive={() => setBroadcastingHere(true)}
                                onStopped={() => setBroadcastingHere(false)} />
              </CardContent>
            </Card>
          </>
        ) : live ? (
          <Card className="bg-slate-900 border-slate-800 text-slate-100" data-testid="live-now-card">
            <CardContent className="p-5">
              <Badge className="bg-rose-600 hover:bg-rose-600 mb-3">
                <span className="mr-1.5 inline-block h-2 w-2 rounded-full bg-white animate-pulse" />
                Live now
              </Badge>
              <h1 className="text-xl font-semibold mb-1" data-testid="live-now-title">{live.title}</h1>
              <p className="text-sm text-slate-400 mb-4">
                {live.player?.kind === 'iframe' || live.player?.kind === 'video'
                  ? 'Playing on your church page.'
                  : (live.player?.reason || 'No watch link saved yet.')}
              </p>

              <div className="space-y-2">
                {publicUrl && (
                  <a href={publicUrl} target="_blank" rel="noopener noreferrer" className="block"
                     data-testid="view-public-page">
                    <Button variant="outline" className="w-full border-slate-700 bg-transparent text-slate-100 hover:bg-slate-800 hover:text-white">
                      <ExternalLink className="h-4 w-4 mr-2" /> See the public page
                    </Button>
                  </a>
                )}
                <Button variant="destructive" className="w-full"
                        disabled={end.isPending}
                        onClick={() => end.mutate(live.id)}
                        data-testid="end-stream-btn">
                  {end.isPending
                    ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    : <Square className="h-4 w-4 mr-2" />}
                  End the stream
                </Button>
              </div>

              {live.webrtc_live ? (
                <p className="mt-5 pt-4 border-t border-slate-800 text-xs text-slate-500">
                  This service is being broadcast from a phone. If that was this
                  phone and the preview has gone, the connection dropped — stop
                  and start it again.
                </p>
              ) : (
              <div className="mt-5 pt-4 border-t border-slate-800">
                <Label className="text-slate-300">Change the watch link</Label>
                <div className="flex gap-2 mt-1.5">
                  <Input value={streamUrl} onChange={(e) => setStreamUrl(e.target.value)}
                         placeholder="Paste the link"
                         className="bg-slate-950 border-slate-700"
                         data-testid="live-update-url" />
                  <Button variant="outline" size="icon" onClick={paste}
                          className="border-slate-700 bg-transparent text-slate-100 shrink-0">
                    <Clipboard className="h-4 w-4" />
                  </Button>
                </div>
                <LinkVerdict url={streamUrl} />
                {streamUrl.trim() && (
                  <Button className="w-full mt-2 bg-slate-100 text-slate-900 hover:bg-white"
                          disabled={updateLink.isPending}
                          onClick={() => updateLink.mutate(live.id)}
                          data-testid="live-update-save">
                    {updateLink.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    Update the link
                  </Button>
                )}
              </div>
              )}
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="mb-5">
              <h1 className="text-2xl font-semibold tracking-tight">Go live</h1>
              <p className="text-sm text-slate-400 mt-1">
                Broadcast from this phone, or paste a link from a platform you
                are already streaming on.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-2 mb-4 p-1 bg-slate-900 rounded-lg">
              <button onClick={() => setMode('camera')}
                      className="flex items-center justify-center gap-1.5 rounded-md py-2 text-sm font-medium text-slate-400 transition-colors"
                      data-testid="mode-camera">
                <Camera className="h-4 w-4" />This phone
              </button>
              <button onClick={() => setMode('link')}
                      className="flex items-center justify-center gap-1.5 rounded-md py-2 text-sm font-medium bg-slate-700 text-white transition-colors"
                      data-testid="mode-link">
                <Link2 className="h-4 w-4" />A link
              </button>
            </div>

            {(
            <Card className="bg-slate-900 border-slate-800 text-slate-100">
              <CardContent className="p-5 space-y-4">
                <div>
                  <Label className="text-slate-300">What are you streaming?</Label>
                  <Input value={title} onChange={(e) => setTitle(e.target.value)}
                         placeholder="Sunday Morning Service"
                         className="mt-1.5 bg-slate-950 border-slate-700"
                         data-testid="go-live-title" />
                </div>

                <div>
                  <Label className="text-slate-300">Watch link</Label>
                  <div className="flex gap-2 mt-1.5">
                    <Input value={streamUrl} onChange={(e) => setStreamUrl(e.target.value)}
                           placeholder="https://youtu.be/… or a Facebook live link"
                           inputMode="url" autoCapitalize="off" autoCorrect="off"
                           className="bg-slate-950 border-slate-700"
                           data-testid="go-live-url" />
                    <Button variant="outline" size="icon" onClick={paste}
                            className="border-slate-700 bg-transparent text-slate-100 shrink-0"
                            data-testid="go-live-paste">
                      <Clipboard className="h-4 w-4" />
                    </Button>
                  </div>
                  <LinkVerdict url={streamUrl} />
                </div>

                <Button className="w-full h-14 text-base bg-rose-600 hover:bg-rose-500"
                        disabled={start.isPending}
                        onClick={() => start.mutate()}
                        data-testid="go-live-btn">
                  {start.isPending
                    ? <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    : <Radio className="h-5 w-5 mr-2" />}
                  Go live
                </Button>

                <p className="text-xs text-slate-500 text-center">
                  Your congregation sees this on the church page straight away.
                  If a service is already scheduled for today, this goes live on that one.
                </p>
              </CardContent>
            </Card>
            )}
          </>
        )}

        <Card className={`mt-4 bg-slate-900 border-slate-800 ${transport.livekit ? '' : 'hidden'}`}>
          <CardContent className="p-5">
            <SimulcastPanel scope="church" ident={churchId} dark />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
